// What a place's figures mean, in one place, so web and phone tell the same story (2026-09-18).
//
// `site_overview` splits the custody ledger into five buckets that add up to `expected`:
//
//     on_site + in_transit + off_site + missing + not_seen = expected
//
// so a card can show "4 / 8 on site" and then account for all four of the remaining assets. `awaiting_receipt` is
// separate on purpose: those are standing here but still on the sender's books, so they are inbound work, not stock.
import type { SiteOverview } from './types';

/** The figures a place card draws. Summing a project's locations sums each bucket — the identity survives. */
export interface PlaceTally {
  /** the custody ledger — the denominator */
  expected: number;
  on_site: number;
  in_transit: number;
  off_site: number;
  missing: number;
  not_seen: number;
  /** inbound, outside `expected` */
  awaiting_receipt: number;
  /** everything standing here whoever owns it */
  present_total: number;
}

const ZERO: PlaceTally = {
  expected: 0,
  on_site: 0,
  in_transit: 0,
  off_site: 0,
  missing: 0,
  not_seen: 0,
  awaiting_receipt: 0,
  present_total: 0,
};

/**
 * Sum one place, or every location of a project. Summing first and only then reading the buckets is what fixes the
 * old per-site clamp, which dropped an asset whose custody was location A but which was sighted at location B.
 */
export function tally(sites: readonly SiteOverview[]): PlaceTally {
  const out = { ...ZERO };
  // `?? 0` on every field: a build talking to a database that has not had the buckets migration yet gets `undefined`
  // for the new columns, and one NaN would blank the whole card
  for (const s of sites) {
    out.expected += s.expected ?? 0;
    out.on_site += s.on_site ?? 0;
    out.in_transit += s.in_transit ?? 0;
    out.off_site += s.off_site ?? 0;
    out.missing += s.missing ?? 0;
    out.not_seen += s.not_seen ?? 0;
    out.awaiting_receipt += s.awaiting_receipt ?? 0;
    out.present_total += s.present_total ?? s.on_site ?? 0;
  }
  return out;
}

/** The buckets that make up the gap between `on_site` and `expected`, biggest worry first, zeroes dropped. */
export type GapKind = 'missing' | 'in_transit' | 'off_site' | 'not_seen';
const GAP_ORDER: GapKind[] = ['missing', 'in_transit', 'off_site', 'not_seen'];

export const GAP_TONE: Record<GapKind, 'error' | 'info' | 'default'> = {
  missing: 'error',
  in_transit: 'info',
  off_site: 'default',
  not_seen: 'default',
};

export function gaps(t: PlaceTally): { kind: GapKind; n: number }[] {
  return GAP_ORDER.map((kind) => ({ kind, n: t[kind] })).filter((g) => g.n > 0);
}

/**
 * True when the buckets reconcile. The database guarantees it per site (migration post-condition); a card can assert
 * it after summing rather than trusting a stale or partially-filtered list.
 */
export const reconciles = (t: PlaceTally): boolean =>
  t.on_site + t.in_transit + t.off_site + t.missing + t.not_seen === t.expected;
